import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Spacing, IconText, Box } from '@reservamos/elements';
import PointsSlider from 'ui/molecules/PointsSlider';
import LoyaltyPricingRow from 'ui/molecules/PointsSlider/LoyaltyPricingRow';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import useDotersSlider from './useDotersSliders';
import usePurchase from '../../../hooks/store/usePurchase';
import { formatDotersPoints } from '../../../utils/doters';

const DotersSlider = () => {
  const { t } = useTranslation(['payment', 'loyalty']);
  const purchase = usePurchase();

  const {
    doters: { profile, wallet, onApplyPointsDiscount, onRemovePointsDiscount, isServiceAvailable },
  } = useLoyaltyPrograms();

  const {
    minPointsToBeEnable,
    isJustFlatFare,
    totalBalance,
    formattedUsableBalance,
    error,
    canApplyPoints,
    handleOnSubmitPoints,
    handleOnChange,
    handleOnDelete,
    handleOnEdit,
    handleOnCancel,
    maxPoints,
    sliderPoints,
    disabled,
  } = useDotersSlider({
    wallet,
    profile,
    onApplyPointsDiscount,
    onRemovePointsDiscount,
    purchaseTotal: purchase.total,
  });

  const { t: tPayment } = useTranslation('payment');

  const headerTitle =
    disabled || isJustFlatFare
      ? tPayment('label.points_applied')
      : tPayment('doters_instructions.select_quantity_to_use');

  const notMaxOrMinPoints = maxPoints <= 0 && !wallet.minPointsToUse;
  const { walletAccrualPoints } = purchase;

  if (
    purchase.walletType !== 'doters' ||
    notMaxOrMinPoints ||
    purchase.isUpdating ||
    profile.availablePoints === undefined // TODO: change this for wallet.isUpdating when works
  ) {
    if (notMaxOrMinPoints && walletAccrualPoints !== 0) {
      let formattedAccrualPoints = formatDotersPoints(walletAccrualPoints);
      if (formattedAccrualPoints) {
        formattedAccrualPoints = `+ ${formattedAccrualPoints}`;
      }

      return (
        <LoyaltyPricingRow
          description={t('loyalty:will_generate')}
          currency={formattedAccrualPoints}
          doterPointsLabel={t('payment:label.doter_points')}
          highlightColor={!!walletAccrualPoints}
        />
      );
    }

    return null;
  }
  if (profile.availablePoints < minPointsToBeEnable)
    return <Text weight="semibold">{tPayment('label.not_enough_doters_points')}</Text>;
  if (maxPoints < minPointsToBeEnable && !isJustFlatFare)
    return <Text weight="semibold">{tPayment('label.can_not_use_doters_points')}</Text>;
  if (!isServiceAvailable && !isJustFlatFare) {
    return <Text weight="semibold">{tPayment('label.can_not_use_doters_points')}</Text>;
  }

  return (
    <Spacing id="dotersSlider" vertical size="L">
      <PointsSlider
        defaultExpanded
        handleOnSubmitPoints={handleOnSubmitPoints}
        maxPoints={maxPoints}
        handleOnChange={handleOnChange}
        handleOnDelete={handleOnDelete}
        handleOnEdit={handleOnEdit}
        handleOnCancel={handleOnCancel}
        sliderPoints={sliderPoints}
        inputPointsValue={sliderPoints}
        balance={totalBalance}
        disabled={disabled || isJustFlatFare}
        canApplyPoints={canApplyPoints}
        pointsApplied={wallet.usedBalance || isJustFlatFare}
        isLoading={wallet.isUpdating}
        headerTitle={headerTitle}
        inputLabelText={tPayment('label.doters_to_use')}
        sliderHeader={
          <Spacing vertical size="S">
            <Spacing justifyContent="center">
              {canApplyPoints ? (
                <Spacing alignItems="center" size="XS">
                  <Box
                    paddingHorizontal="S"
                    paddingVertical="XXS"
                    bgColor="grayBorder"
                    alphaBg="L"
                    borderRadius="M"
                  >
                    <Text weight="bold" size="L">
                      {formatDotersPoints(sliderPoints)}
                    </Text>
                  </Box>
                  <Text>{t('payment:label.doters_to_use')}</Text>
                </Spacing>
              ) : (
                <Text color="loyalty" weight="bold" size="L" mobileSize="M">
                  {tPayment('label.you_have_doters', {
                    points: formattedUsableBalance,
                  })}
                </Text>
              )}
            </Spacing>

            {Boolean(wallet.minPointsToUse) && (
              <IconText
                iconType="checkGreen"
                label={tPayment('label.points_applied_flat_fare', {
                  pointsUsed: formatDotersPoints(wallet.minPointsToUse),
                  context: 'quantity',
                })}
                iconSize="M"
                fontSize="M"
              />
            )}
          </Spacing>
        }
        minPointsText={`0 ${tPayment('label.doter_points_min')}`}
        maxPointsText={`${formatDotersPoints(maxPoints)} ${tPayment('label.doter_points_max')}`}
        pointsAppliedText={sliderPoints}
        error={error}
        editButtonEnabled={!isJustFlatFare}
        deleteButtonEnabled={!isJustFlatFare}
        modifierClass="doters"
        loyalty="doters"
        hasHeaderColor
        showIconCheck={false}
        hideModifiersButtons={isJustFlatFare}
        availablePoints={profile.availablePoints}
      />
    </Spacing>
  );
};

export default DotersSlider;
